import { FC } from "react";
import { Link } from "react-router-dom";
import { DataTable, DataTableColumn } from "../../../design-system";
import { ExpirationDateCell } from "../../../common/components/ExpirationDateCell";
import { CategoryPath, categoryLabel } from "../../../common/components/CategoryPath";
import { useOrgCategories } from "../../../common/useOrgCategories";
import {
    dealStatusPresentation,
    StatusProgressCell,
} from "../../project/components/ProjectStatusCell";
import { ProjectCategoryWidget } from "../../project/components/ProjectCategoryWidget";
import { OwnerCell } from "../../../common/components/OwnerCell";
import { reassignProjectOwner } from "../../../common/api/ProjectClient";
import { canDoOnDeal } from "../../../types/OrderFormTypes";
import { ProjectAction } from "../../../types/ProjectTypes";
import { DealNode } from "../list/vendorSpend";
import { AverageScoreCell, SavingsCell, TotalValueCell } from "../list/components/SpendCells";
import classes from "./VendorDealsTable.module.less";

interface VendorDealsTableProps {
    deals: DealNode[];
    /** Called after a deal's category or owner changed, so the list reloads. */
    onUpdate: () => void;
}

const dealName = (node: DealNode) => node.deal.name || node.vendor.name;

const dealPath = (node: DealNode) =>
    node.deal.projectId ? `/project/${node.deal.projectId}` : `/order/${node.deal.id}`;

/** A vendor's deals: their category (as on the procurement hub), status and numbers. */
export const VendorDealsTable: FC<VendorDealsTableProps> = ({ deals, onUpdate }) => {
    // Only orgs with their own taxonomy have a Category column.
    const categories = useOrgCategories();

    const categoryColumn: DataTableColumn<DealNode>[] = categories
        ? [
              {
                  key: "category",
                  title: "Category",
                  sort: "text",
                  sortValue: (node) =>
                      node.category.path ? categoryLabel(node.category.path) : undefined,
                  // The category lives on the deal's project, editable by
                  // whoever may edit it; a deal without one shows its
                  // category read-only.
                  render: (node) =>
                      node.deal.projectId ? (
                          <ProjectCategoryWidget
                              projectId={node.deal.projectId}
                              categoryPath={node.category.path}
                              categories={categories}
                              canEdit={canDoOnDeal(node.deal, ProjectAction.EDIT)}
                              onUpdate={onUpdate}
                          />
                      ) : (
                          <CategoryPath path={node.category.path} />
                      ),
              },
          ]
        : [];

    const columns: DataTableColumn<DealNode>[] = [
        {
            key: "deal",
            title: "Deal",
            width: "20%",
            ellipsis: true,
            sort: "text",
            sortValue: dealName,
            render: (node) => (
                <Link to={dealPath(node)} title={dealName(node)} className={classes.deal}>
                    {dealName(node)}
                </Link>
            ),
        },
        ...categoryColumn,
        {
            key: "owner",
            title: "Owner",
            sort: "text",
            sortValue: (node) => node.deal.projectOwner?.name,
            // The owner is the project's, editable by whoever may reassign it, as on the hub.
            render: (node) =>
                node.deal.projectId ? (
                    <OwnerCell
                        ownerId={node.deal.projectOwner?.id}
                        ownerName={node.deal.projectOwner?.name}
                        canEdit={canDoOnDeal(node.deal, ProjectAction.REASSIGN)}
                        onSave={async (ownerId) => {
                            await reassignProjectOwner(node.deal.projectId!, ownerId);
                        }}
                        onUpdate={onUpdate}
                    />
                ) : null,
        },
        {
            key: "status",
            title: "Status",
            // Pipeline order: not started, in negotiation, ready, signed.
            sort: "number",
            sortValue: (node) => dealStatusPresentation(node.deal.negotiationStatus)?.stage,
            render: (node) => (
                <StatusProgressCell status={dealStatusPresentation(node.deal.negotiationStatus)} />
            ),
        },
        {
            key: "score",
            title: "Score",
            sort: "number",
            sortValue: (node) => node.score,
            render: (node) => <AverageScoreCell tally={node} />,
        },
        {
            key: "value",
            title: "Baseline TCV",
            sort: "number",
            sortValue: (node) => node.value,
            render: (node) => <TotalValueCell tally={node} />,
        },
        {
            key: "savings",
            title: "Savings",
            sort: "number",
            sortValue: (node) => node.realizedSavings,
            render: (node) => <SavingsCell tally={node} deal={node.deal} />,
        },
        {
            key: "expiration",
            title: "Expiration",
            sort: "date",
            sortValue: (node) => node.deal.orderEndDate,
            render: (node) => (
                <ExpirationDateCell
                    date={node.deal.orderEndDate?.toString()}
                    orderFormId={node.deal.id}
                />
            ),
        },
    ];

    return (
        <DataTable<DealNode>
            columns={columns}
            rows={deals}
            rowKey={(node) => node.deal.id}
            searchText={dealName}
            searchPlaceholder="Search deals"
            emptyText="No deals yet"
            dense
        />
    );
};
